/**
 * BackOrderAdjustModal — create/view/adjust the back-order quantity for one product.
 * Opened from the "Create Back Order" / "View Back Order" buttons on the product card and detail
 * page. Quantities are uncapped (back order = demand beyond stock). Edits are local until the Done
 * button is pressed, which persists the back order via useBackOrder (closing without Done discards).
 */
import { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ProductQuantityCalculator } from "../../utils/ProductQuantityCalculator";
import QuantityInput from "../cart/QuantityInput";
import { useBackOrder } from "../../hooks/useBackOrder";
import { formatCurrency } from "../../utils/formatters";

const BackOrderAdjustModal = ({ show, onHide, product }) => {
  const { getItemBackOrderQuantity, setBackOrderQuantity } = useBackOrder();

  const calculator = useMemo(
    () => (product ? new ProductQuantityCalculator(product) : null),
    [product?.id, product?.unitsPerCase, product?.optSellByTypeID],
  );

  const currentQty = getItemBackOrderQuantity(product?.id);
  const display = useMemo(
    () => (calculator ? calculator.unitsToDisplay(currentQty) : { cases: 0, units: 0 }),
    [calculator, currentQty],
  );
  const [cases, setCases] = useState(display.cases);
  const [units, setUnits] = useState(display.units);
  const [isSaving, setIsSaving] = useState(false);

  // Seed the inputs from the saved back order each time the modal opens (or the product changes),
  // discarding any edits from a previous open. Intentionally NOT depending on `display` so typing
  // isn't clobbered — nothing persists until Done.
  useEffect(() => {
    if (show) {
      setCases(display.cases);
      setUnits(display.units);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, product?.id]);

  // Persist only when Done is pressed (creating/updating/removing the back order in one shot).
  const handleDone = async () => {
    if (!calculator || !product) {
      onHide();
      return;
    }
    const desired = calculator.displayToUnits(cases, units);
    if (desired === currentQty) {
      onHide();
      return;
    }
    setIsSaving(true);
    try {
      await setBackOrderQuantity(product.id, desired, product);
      onHide();
    } catch (error) {
      console.error("Failed to save back order quantity:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!show || !product) return null;

  // Uncapped input: strip unitAvailable so the quantity isn't clamped to stock.
  const inputProduct = {
    id: product.id,
    unitsPerCase: product.unitsPerCase,
    optSellByTypeID: product.optSellByTypeID,
  };
  // Pending (unsaved) quantity — the total reflects what will be saved on Done.
  const pendingQty = calculator ? calculator.displayToUnits(cases, units) : 0;
  const pendingPrice = calculator?.calculatePrice(pendingQty);

  return createPortal(
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1065 }}>
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-clock-history me-2"></i>
                Back Order
              </h5>
              <button type="button" className="btn-close" onClick={onHide} aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <div className="fw-medium">{product.name}</div>
              {product.flavor && <div className="text-muted small mb-2">{product.flavor}</div>}
              <p className="text-muted small">
                Order more than is in stock — the extra is placed on back order and shipped when it&apos;s
                restocked. Press <strong>Done</strong> to save.
              </p>

              <div className="row">
                {calculator?.canSellByCase && (
                  <div className="col-6">
                    <label className="form-label">{calculator?.caseLabelPlural || "Cases"}</label>
                    <QuantityInput
                      value={cases}
                      onChange={setCases}
                      quantityType="cases"
                      product={inputProduct}
                      currentUnitQuantity={units}
                      disabled={isSaving}
                      isUpdating={isSaving}
                    />
                  </div>
                )}
                {calculator?.canSellByUnit && product.unitsPerCase > 1 && (
                  <div className="col-6">
                    <label className="form-label">Individual {calculator?.unitLabelPlural || "Units"}</label>
                    <QuantityInput
                      value={units}
                      onChange={setUnits}
                      quantityType="units"
                      product={inputProduct}
                      currentCaseQuantity={cases}
                      disabled={isSaving}
                      isUpdating={isSaving}
                    />
                  </div>
                )}
              </div>

              {pendingQty > 0 && pendingPrice && (
                <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-3">
                  <span className="fw-medium">Back order total ({pendingQty}):</span>
                  <strong className="text-stash-accent">{formatCurrency(pendingPrice.grandTotal)}</strong>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-dark" onClick={handleDone} disabled={isSaving}>
                {isSaving ? "Saving…" : "Done"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
    </>,
    document.body,
  );
};

export default BackOrderAdjustModal;
